/**
 * src/components/primitives/FreshnessBanner.tsx — honest-state freshness
 * primitive (FR-007 + FR-016).
 *
 * Wired primitive that consumes the typed `useFreshness(cronKey)` hook
 * (`src/hooks/useFreshness.ts`) and renders a visible warning banner
 * ONLY when staleness is real — the underlying `getLastRunForTask`
 * returned a numeric timestamp AND `now() - lastRunAt` exceeds the
 * per-key threshold in `CRON_THRESHOLDS_MS`.
 *
 * The three documented states:
 *
 *   1) LOADING (`lastRunAt === undefined`, `isStale === false`)
 *      → render `null`. A "data is stale" warning on first paint, before
 *      the Convex subscription has resolved, would be a false alarm.
 *
 *   2) FRESH (`isStale === false` with a real `lastRunAt`)
 *      → render `null`. The cron has been observed and is within
 *      threshold; the operator never sees a ghost banner on a healthy
 *      surface.
 *
 *   3) STALE (`isStale === true`, real `lastRunAt`)
 *      → render the visible warning banner with a live-region signal so
 *      assistive tech announces it. This is the ONLY state the banner
 *      renders.
 *
 * Compile-time guarantee: the `cronKey` prop is the closed `FreshnessKey`
 * literal union (eleven keys verbatim from `freshness_facts.md` §2 /
 * `useFreshness.ts`'s `CRON_THRESHOLDS_MS`). A 12th key fails the
 * compile — the inverse silent-defect the PRD names (a banner bound to a
 * phantom key would fire NEVER, hiding real staleness; FR-007).
 *
 * RED-on-regression: a banner that fires while `lastRunAt === undefined`,
 * or hides while truly stale, or accepts a phantom string key, fails
 * `tests/primitives/freshness-banner.test.ts` (T309 → T310).
 */
import React, { type CSSProperties } from 'react';
import { useFreshness, type FreshnessKey } from '../../hooks/useFreshness';

/**
 * Wired-mode props — the canonical v.3 shape: bind a closed-union `cronKey`
 * and let the primitive measure staleness via `useFreshness()`.
 */
interface FreshnessBannerWiredProps {
  cronKey: FreshnessKey;
  /** Optional override for the auto-generated stale-age copy. */
  message?: string;
  /** Optional refresh affordance rendered alongside the warning. */
  onRefresh?: () => void;
}

/**
 * Legacy "show + message" shape — the v.3 PROTOTYPE callers (screen
 * `_bundle.tsx` files) pass `<FreshnessBanner show message="..." />` with
 * no measurement. Kept temporarily so the screens that have not yet been
 * wired (T311+) continue to compile; once every screen passes a real
 * `cronKey`, this branch can be deleted in a single sweep. The legacy
 * shape renders the banner whenever `show` is truthy — no measurement.
 *
 * @deprecated Use `cronKey` to bind a real freshness query.
 */
interface FreshnessBannerLegacyProps {
  cronKey?: undefined;
  show?: boolean;
  message: string;
  onRefresh?: () => void;
}

export type FreshnessBannerProps =
  | FreshnessBannerWiredProps
  | FreshnessBannerLegacyProps;

function defaultMessage(ageMs: number): string {
  // Operator-language copy (Spanish — see CLAUDE.md §12, FR-018 glossary
  // gate). Minutes resolution is plenty for an operator alarm; the exact
  // age is power-user info shown via the timer chip the panel may render.
  const minutes = Math.round(ageMs / 60_000);
  if (minutes < 60) {
    return `Datos sin actualizar desde hace ${minutes} min.`;
  }
  const hours = Math.round(minutes / 60);
  return `Datos sin actualizar desde hace ${hours} h.`;
}

const BANNER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  background: 'rgba(245,158,11,0.08)',
  border: '1px solid rgba(245,158,11,0.3)',
  borderRadius: 22,
  marginBottom: 20,
};

export function FreshnessBanner(
  props: FreshnessBannerProps,
): React.ReactElement | null {
  // Legacy `show + message` branch — preserved temporarily for prototype
  // screens that have not yet been wired to a real `cronKey` (see
  // `FreshnessBannerLegacyProps` above). The legacy branch does NO
  // measurement: it renders iff `show` is truthy.
  if (props.cronKey === undefined) {
    if (!props.show) return null;
    return renderBanner(props.message, props.onRefresh, undefined);
  }

  // Wired branch (the canonical v.3 shape). Calls the hook unconditionally
  // so the React rules-of-hooks invariant is preserved across both
  // branches: the legacy branch never reaches this line.
  return <WiredFreshnessBanner {...props} />;
}

function WiredFreshnessBanner({
  cronKey,
  message,
  onRefresh,
}: FreshnessBannerWiredProps): React.ReactElement | null {
  const { isStale, ageMs } = useFreshness(cronKey);

  // States 1 + 2 (LOADING or FRESH) → render NOTHING.
  if (!isStale || ageMs === undefined) {
    return null;
  }

  // State 3 (STALE) → render the banner.
  const copy = message ?? defaultMessage(ageMs);
  return renderBanner(copy, onRefresh, cronKey);
}

function renderBanner(
  copy: string,
  onRefresh: (() => void) | undefined,
  cronKey: string | undefined,
): React.ReactElement {

  return (
    <div
      role="status"
      aria-live="polite"
      style={BANNER_STYLE}
      data-circa-freshness-banner="stale"
      data-cron-key={cronKey}
    >
      <span
        aria-hidden="true"
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: 'rgba(245,158,11,0.6)',
          display: 'inline-block',
        }}
      />
      <span
        style={{
          fontSize: 14,
          color: 'var(--body-muted)',
          flex: 1,
        }}
      >
        {copy}
      </span>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          style={{
            border: '1px solid var(--border-cool)',
            background: 'transparent',
            color: 'var(--ink-muted)',
            padding: '6px 12px',
            fontSize: 13,
            borderRadius: 'var(--radius-pill)',
            cursor: 'pointer',
          }}
        >
          Actualizar
        </button>
      )}
    </div>
  );
}

export default FreshnessBanner;
